import type { ConnectResult, GoogleProfile } from '../../../src/shared/types';
import { getSupabase } from './supabaseClient';
import { calendarAccounts, forgetCalendarAccounts, startCalendarConnect } from '../calendarAccounts';

/**
 * `googleAuth.ts`'nin web karşılığı. Masaüstünde Google yetkisi bu cihazda
 * (loopback OAuth + şifreli dosya); web'de girişi Supabase yapıyor, takvim
 * yetkisi sunucuda (bkz. calendarAccounts.ts). Profil (ad, fotoğraf) giriş
 * yapılan Google hesabından geliyor.
 */

export class AuthError extends Error {
  constructor(public readonly code: 'not_connected' | 'auth_expired' | 'network') {
    super(code);
    this.name = 'AuthError';
  }
}

export function invalidateAccessToken(): void {
  forgetCalendarAccounts();
}

/** Web'de Google erişim anahtarı tarayıcıya hiç gelmiyor. */
export async function getAccessToken(): Promise<string> {
  throw new AuthError('not_connected');
}

export async function fetchProfile(): Promise<GoogleProfile | undefined> {
  return getOrRefreshProfile();
}

/** Giriş yapılan hesabın Google profili (Supabase kimlik bilgilerinden). */
export async function getOrRefreshProfile(): Promise<GoogleProfile | undefined> {
  const { data } = await getSupabase().auth.getSession();
  const user = data.session?.user;
  if (!user) return undefined;
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const text = (key: string) => (typeof meta[key] === 'string' ? (meta[key] as string).trim() : '');
  const email = user.email ?? text('email');
  const picture = text('avatar_url') || text('picture');
  return {
    email,
    name: text('full_name') || text('name') || email,
    // Masaüstünde gömülü data: adresi; web'de Google'ın https adresi (CSP img-src'de izinli).
    pictureDataUrl: /^https:\/\//.test(picture) ? picture : undefined,
  };
}

/** Takvimi bağla / yeniden bağla — sayfa Google'a gider (bkz. startCalendarConnect). */
export async function connectGoogle(): Promise<ConnectResult> {
  return startCalendarConnect('google');
}

export function takeIdToken(): { idToken: string | null; accessToken: string | null } {
  return { idToken: null, accessToken: null };
}

/**
 * Masaüstünde çıkışta ve hesap silmede çağrılıyor: bu cihazdaki Google
 * yetkisini temizler. Web'de cihazda yetki yok — sunucudaki takvim bağlantısı
 * çıkışta kalmalı (otomatik bot çalışmaya devam etsin); yalnızca önbellek
 * temizlenir. Bağlantıyı kesmek köprüdeki `disconnectGoogleCalendar`.
 */
export async function disconnectGoogle(): Promise<void> {
  forgetCalendarAccounts();
}

/** Takvim bağlı mı (sunucu). */
export async function isGoogleCalendarConnected(): Promise<boolean> {
  const { google } = await calendarAccounts().catch(() => ({ google: { connected: false, expired: false } }));
  return google.connected && !google.expired;
}
